import { useEffect, useState } from 'react';

import PropTypes from 'prop-types';

import { ModalDisplayedTracker } from '@zenbusiness/application-commons-clickstream';
import Divider from '@zenbusiness/zui/Divider';
import LoadingSpinner from '@zenbusiness/zui/LoadingSpinner';
import Text from '@zenbusiness/zui/Text';

import { useCompletionsMutation } from '#client/hooks/useCompletionsMutation';
import { usePromptQuery } from '#client/hooks/usePromptQuery';

import {
  CompletionsContainer,
  SlideInFooter,
  StyledSlide,
  FooterTitle,
  FooterDescription,
  ContentWrapper,
} from './AiSlideIn.style';
import AiSuggestion from './AiSuggestion';
import { PromotionalBanner } from './PromotionalBanner';

import { MarkdownRenderer } from '../MarkdownRenderer';

/**
 * Maps the queryType to the content renderer.
 * The content renderer decides the content of the ai slide in.
 */
const ContentRenderers = {
  smartAssist: ({ parsedData, reasonsReplacements }) => {
    const { reasons, introduction, conclusion } = parsedData;
    return (
      <AiSuggestion items={reasons} titleReplacements={reasonsReplacements} header={introduction} body={conclusion} />
    );
  },
  requirements: ({ parsedData, reasonsReplacements }) => {
    const { introduction, conclusion } = parsedData;
    return (
      <AiSuggestion titleReplacements={reasonsReplacements} header={introduction} body={conclusion} showList={false} />
    );
  },
  article: ({ parsedData }) => {
    const { article } = parsedData;
    return <MarkdownRenderer>{article}</MarkdownRenderer>;
  },
};

/**
 * Maps the parsed data to the content type.
 * The content type is used to determine the content renderer.
 * @param {object} parsedData - The parsed data from the completions.
 * @returns {string} The content type.
 */
const getContentType = (parsedData) => {
  if (parsedData?.article) {
    return 'article';
  }
  if (parsedData?.reasons && parsedData?.conclusion && parsedData?.introduction) {
    return 'smartAssist';
  }
  if (parsedData?.conclusion && parsedData?.introduction && !parsedData?.reasons) {
    return 'requirements';
  }
  return 'smartAssist';
};

function AiSlideIn({
  title,
  overline,
  subtitle,
  reasonsReplacements,
  promptSlug,
  queryType,
  fillData,
  aiSlideInOpen,
  closeHandler,
  eventingProperties,
  errorMessage,
  footer,
  banner,
}) {
  const [completionsFetched, setCompletionsFetched] = useState(false);
  const {
    data: promptData,
    loading: promptLoading,
    error: promptError,
  } = usePromptQuery({
    promptSlug,
    queryType,
  });

  const {
    fetchCompletions,
    data: completionsData,
    loading: completionsLoading,
    error: completionsError,
  } = useCompletionsMutation({ queryType });

  useEffect(() => {
    if (promptData && !promptLoading && !completionsFetched) {
      fetchCompletions(promptData, fillData);
      setCompletionsFetched(true);
    }
  }, [promptData, promptLoading, fillData, completionsFetched, completionsData, fetchCompletions]);

  let content;

  if (promptError || completionsError) {
    content = <Text>{errorMessage}</Text>;
  } else if (promptLoading || completionsLoading) {
    content = <LoadingSpinner />;
  } else {
    const parsedData = completionsData?.completions?.messages[0]?.parsed || {};
    const contentType = getContentType(parsedData);
    const ContentRenderer = ContentRenderers[contentType];

    const { aiProjectName, elementName, elementText, elementValue } = eventingProperties;
    content = (
      <>
        <ContentRenderer parsedData={parsedData} reasonsReplacements={reasonsReplacements} />
        <ModalDisplayedTracker
          open={aiSlideInOpen && completionsData}
          elementName={elementName || 'smart_assistant'}
          elementText={elementText}
          elementValue={elementValue}
          dynamicProperties={{
            ai_project_name: aiProjectName,
          }}
        />
      </>
    );
  }

  return (
    <StyledSlide
      title={title}
      overline={overline}
      subtitle={subtitle}
      open={aiSlideInOpen}
      onClose={closeHandler}
      isLoading={promptLoading || completionsLoading}
    >
      <CompletionsContainer isLoading={promptLoading || completionsLoading}>
        <ContentWrapper>
          {content}
          {banner && completionsData && !promptLoading && !completionsLoading && (
            <PromotionalBanner
              title={banner.title}
              description={banner.description}
              ctaText={banner.ctaText}
              onCtaClick={banner.onCtaClick}
            />
          )}
        </ContentWrapper>
      </CompletionsContainer>
      <SlideInFooter>
        <Divider />
        <FooterTitle weight="medium" gutterBottom size="xs">
          ZenBusiness AI
        </FooterTitle>
        <FooterDescription size="xs">{footer}</FooterDescription>
      </SlideInFooter>
    </StyledSlide>
  );
}

AiSlideIn.propTypes = {
  title: PropTypes.string,
  overline: PropTypes.string,
  subtitle: PropTypes.string,
  reasonsReplacements: PropTypes.objectOf(PropTypes.string),
  promptSlug: PropTypes.string.isRequired,
  queryType: PropTypes.string,
  fillData: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool, PropTypes.object, PropTypes.array]),
  ).isRequired,
  aiSlideInOpen: PropTypes.bool.isRequired,
  closeHandler: PropTypes.func.isRequired,
  eventingProperties: PropTypes.shape({
    elementName: PropTypes.string.isRequired,
    elementText: PropTypes.string,
    elementValue: PropTypes.string,
    aiProjectName: PropTypes.string.isRequired,
  }),
  errorMessage: PropTypes.string,
  footer: PropTypes.string,
  banner: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    ctaText: PropTypes.string,
    onCtaClick: PropTypes.func,
  }),
};

export default AiSlideIn;
